import { useAuth } from '@/app/context';
import { LoadingSpinner } from '@/shared/ui';
import { Button, Text } from '@noorden-insurtech/alba-design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import parse, { type DOMNode, Element, domToReact } from 'html-react-parser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAcceptTerms } from '../../features/auth/hooks/use-accept-terms.hook';
import { useTerms } from '../../features/auth/hooks/use-terms.hook';

export const Route = createFileRoute('/_auth/terms')({
  component: TermsPage,
  pendingComponent: LoadingSpinner,
});

function TermsPage() {
  const navigate = useNavigate();
  const [requiresScroll, setRequiresScroll] = useState(false);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { data: termsData, isLoading, error, refetch } = useTerms();
  const { logout } = useAuth();
  const { mutateAsync: acceptTermsMutation } = useAcceptTerms();

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const shouldRequireScroll = el.scrollHeight - el.clientHeight > 2;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;

    setRequiresScroll(shouldRequireScroll);
    setHasReachedBottom(!shouldRequireScroll || reachedBottom);
  }, []);

  const handleScroll = () => {
    updateScrollState();
  };

  useEffect(() => {
    const id = requestAnimationFrame(updateScrollState);

    return () => cancelAnimationFrame(id);
  }, [termsData, updateScrollState]);

  useEffect(() => {
    const handleResize = () => updateScrollState();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [updateScrollState]);

  const canAccept = !requiresScroll || hasReachedBottom;

  const handleReject = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  const handleAcceptTerms = async () => {
    try {
      const result = await acceptTermsMutation({
        termsVersion: termsData?.data?.version || '1.0.0',
      });

      if (result.success) {
        navigate({ to: '/dashboard' });
      }
    } catch (error) {
      console.error('Erro ao aceitar termos:', error);
    }
  };

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center py-8 gap-4'>
        <Text size='lg' className='text-[#c54d4d] text-center'>
          Erro ao carregar termos de uso
        </Text>
        <Button variant='primary' size='large' onClick={() => refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className='w-full bg-white rounded-[16px] p-6 shadow-[0px_4px_4px_0px_rgba(15,22,30,0.08)] max-h-[420px] overflow-y-auto text-[#23262B]'
      >
        <div className='flex flex-col gap-4'>
          <Text as='h2' size='xl' className='font-bold text-2xl'>
            {termsData?.data?.title || 'Termo De Aceite'}
          </Text>
          {isLoading ? (
            <div className='py-8 flex items-center justify-center'>
              <LoadingSpinner />
            </div>
          ) : (
            <div>
              {parse(termsData?.data?.content || 'Carregando conteúdo...', {
                replace(domNode) {
                  if (
                    domNode instanceof Element &&
                    (domNode.name === 'h1' || domNode.name === 'h2')
                  ) {
                    const Tag = domNode.name;
                    return (
                      <Tag style={{ fontWeight: 700 }}>
                        {domToReact(domNode.children as DOMNode[])}
                      </Tag>
                    );
                  }
                },
              })}
            </div>
          )}
        </div>
      </div>

      <div className='flex items-center gap-4 w-full pt-4'>
        <Button
          variant='secondary'
          size='large'
          onClick={() => handleReject()}
          className='w-full bg-transparent text-[#2C2864] border border-[#2C2864] hover:border-[#2C2864] hover:text-[#2C2864] hover:bg-transparent transition-colors'
        >
          Rejeitar
        </Button>
        <Button
          variant='primary'
          size='large'
          disabled={!canAccept}
          onClick={handleAcceptTerms}
          className='w-full'
        >
          Aceitar
        </Button>
      </div>
    </>
  );
}
